import { useEffect, useState } from "react";
import { JOIN_CODE_LENGTH, normalizeJoinCode } from "@vocifuchs/shared";
import { api, ApiError } from "./api.js";
import { errors } from "../copy/errors.js";

/**
 * Class invitations as a link (issue #48). The code travels in the fragment,
 * `/register?join#KLASSE5B`: browsers never send the fragment to a server, so
 * the code reaches no access log -- ours or anyone's in between.
 */
export function joinLink(code: string, origin: string = window.location.origin): string {
  return `${origin}/register?join#${code}`;
}

/** The code from a link's fragment, or "" when there is none that fits. */
export function codeFromHash(hash: string): string {
  const code = normalizeJoinCode(decodeURIComponent(hash.replace(/^#/, "")));
  return code.length === JOIN_CODE_LENGTH ? code : "";
}

/**
 * The code field's state, filled from the link and kept in step with it: a
 * link opened again in the same tab only changes the fragment, without a
 * reload, and must still replace whatever was typed before.
 */
export function useCodeFromLink(): [string, (code: string) => void] {
  const [code, setCode] = useState(() => codeFromHash(window.location.hash));
  useEffect(() => {
    const follow = () => {
      const next = codeFromHash(window.location.hash);
      if (next) setCode(next);
    };
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);
  return [code, setCode];
}

export type ClassPreview =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "found"; name: string }
  | { state: "unknown"; message: string };

/**
 * Which class a complete code belongs to (issue #42), asked as soon as eight
 * characters are there. Joining waits for a name: a child sees which class
 * they are joining before it counts, not after.
 */
export function useClassPreview(code: string): ClassPreview {
  const [preview, setPreview] = useState<ClassPreview>({ state: "idle" });
  useEffect(() => {
    if (code.length !== JOIN_CODE_LENGTH) {
      setPreview({ state: "idle" });
      return;
    }
    let current = true;
    setPreview({ state: "checking" });
    api<{ name: string }>("/api/classes/preview", {
      method: "POST",
      body: JSON.stringify({ join_code: code }),
    })
      .then((data) => current && setPreview({ state: "found", name: data.name }))
      .catch((err: unknown) => {
        if (current) {
          setPreview({ state: "unknown", message: err instanceof ApiError ? err.message : errors.failed });
        }
      });
    return () => {
      current = false;
    };
  }, [code]);
  return preview;
}
